/* eslint-disable no-nested-ternary */
import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent, JSX } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { CreateInteracHandler } from './types';
import { buildInteracRows } from './buildInteracRows.helper';
import styles from './InteracDialogue.module.scss';

const { Box, Table, TextField, Select, Toggle, Icon, SubTitle, Button } = vp;

type CreateInteracProps = {
  handlers: CreateInteracHandler;
  defaultValues: vp.InteracAddEmailRequest;
};

type FC = FunctionComponent<CreateInteracProps>;
const InteracForm: FC = ({ defaultValues, handlers }: CreateInteracProps) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Accounts.MyBankAccounts.InteracDialogue.InteracForm',
  });
  const interacRows = buildInteracRows();
  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={handlers.onCreateInterac}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
      enableReinitialize
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit }) => {
        const interacTable: vp.TableProps = {
          name: 'withdraw-account-fields',
          variant: 'pair',
          columns: {
            Field: { header: t('labels') || '' },
            Input: { header: t('fields') || '' },
          },
          rows: interacRows
            .filter((x) => x.visible(values))
            .map(({ label, id, require, preset, type, placeholder, getSelectOptions, maxLength }) => {
              let fieldComponent;

              const fieldLabel = (
                <Box jc="fs" flx="col">
                  <Box ai="center">
                    {label} {getRequiredIcon(require)}
                  </Box>
                </Box>
              );

              if (type === 'input') {
                fieldComponent = (
                  <TextField
                    preset={preset}
                    key={id}
                    name={id}
                    value={String(values[id])}
                    variant="standard"
                    placeholder={placeholder}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    maxLength={maxLength}
                    error={errors[id] && touched[id] ? errors[id] : null}
                  />
                );
              } else if (type === 'select') {
                fieldComponent = (
                  <Select
                    variant="standard"
                    options={getSelectOptions(values)}
                    key={id}
                    name={id}
                    value={String(values[id])}
                    placeholder={placeholder}
                    noOrdering
                    onBlur={handleBlur}
                    onChange={(e: { target: { value: string } }) => {
                      handleChange(e);
                    }}
                    error={errors[id] && touched[id] ? errors[id] : null}
                  />
                );

                // }
              } else {
                fieldComponent = (
                  <Box width="100%" jc="fe">
                    <Toggle
                      name={id}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={Boolean(values[id])}
                      error={errors[id] && touched[id] ? errors[id] : null}
                    />
                  </Box>
                );
              }
              return {
                Field: fieldLabel,
                Input: fieldComponent,
              };
            }),
        };

        return (
          <form onSubmit={handleSubmit}>
            <Box jc="sb">
              <SubTitle subTitle={t('accountDetails') || ''} />
              <Button
                id="interac-close"
                iconLocation="center"
                iconSize="xlg"
                color="secondary"
                label=""
                icon="cross"
                background="#FFFFFF"
                height="12px"
                width="12px"
                iconWidth="12px"
                iconHeight="12px"
                textSize={12}
                fontUnit="px"
                noShadow
                noPadding
                onClick={handlers.onClose}
              />
            </Box>
            <div className={styles.FormTableOuterContainer}>
              <div className={styles.FormTableContainer}>
                <Table {...interacTable} />
              </div>
            </div>
            <Box jc="fe" mt="md">
              <Button type="submit" id="submit-interac" label={t('next')} />
            </Box>
          </form>
        );
      }}
    </Formik>
  );

  function createdValidationSchema() {
    return Yup.object().shape({
      RecipientName: Yup.string().required(t('required') || ''),
      RecipientEmailAddress: Yup.string().when('etransferConnectionType', {
        is: 'email',
        then: Yup.string()
          .email(t('invalidEmail') || '')
          .required(t('required') || ''),
        otherwise: Yup.string(),
      }),
      RecipientPhoneNumber: Yup.string().when('etransferConnectionType', {
        is: 'phone',
        then: Yup.string().required(t('required') || ''),
        otherwise: Yup.string(),
      }),
    });
  }
  function getRequiredIcon(isRequired: boolean): JSX.Element {
    return isRequired ? <Icon icon="asterisk" size="xsm" color="error" /> : <></>;
  }
};

export default InteracForm;
